<template>
  <CstuModal
    id="planner-notes-modal"
    hide-footer
    centered
    dialog-class="xl:!max-w-[41rem]"
    no-close-on-backdrop
    :scrollable="true"
    @hidden="closeModal"
    :z-index="1049"
  >
  <template v-slot:modal-title>
    <div>
      <!--        modal title     -->
      <div class="w-full flex items-center gap-x-[8px]">
        <p class="font-medium text-lg text-[#202324] select-none">{{ note.id ? 'Edit Note' : 'Add Note' }}</p>
      </div>
      <div class="w-full flex items-center gap-x-[8px]">
        <p class="text-sm text-[#757A8A] select-none">Notes added here will be visible in the Calendar View only for now. <a
            href="#"
            class="beacon"
            data-beacon-article-modal="6746c3068c083456b657df04">Learn more</a></p>
      </div>
    </div>
  </template>
    <div class="relative">
      <!--      main modal body     -->
      <div class="w-full flex flex-col justify-center items-center px-1">
        <div class="w-full space-y-6">
          <div class="w-full">
            <label for="title" class="block mb-2 font-medium text-gray-900">Title <span class="text-red-600">*</span></label>
            <input
                id="title"
                type="text"
                v-model="note.note_title"
                @input="(e) => updateNoteField('note_title', e.target.value)"
                placeholder="Add your title here"
                class="w-full border border-gray-300 rounded-lg !text-gray-900 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-100 color-border cst-editor"
            />
            <p v-if="errors.note_title.error" class="mt-1 text-sm text-red-600">{{ errors.note_title.message }}</p>
          </div>
          <div>
            <label for="content" class="block text-sm font-medium text-gray-900 mb-2">Description</label>
            <textarea
                id="description"
                v-model="note.description"
                @input="(e) => updateNoteField('description', e.target.value)"
                placeholder="Add your note body here (optional)"
                rows="4"
                class="w-full border border-gray-300 rounded-lg !text-gray-900 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-100 color-border cst-editor"
            ></textarea>
          </div>
          <div class="flex justify-between space-x-4">
            <div class="w-full">
              <label for="start_date" class="block text-sm font-medium text-gray-900 mb-2">Start Date <span class="text-red-600">*</span></label>
              <CstInputFields
                  :date-options="dateOptions"
                  :value="note.start_date"
                  class="planner-date-picker cursor-pointer w-full"
                  placeholder="Select specific date range"
                  type="date"
                  :range="false"
                  :date-picker-popup-classes="['planner-date-picker-popup']"
                  :enable-auto-close="true"
                  @change="(e) => updateNoteField('start_date', e)"
              />
              <p v-if="errors.start_date.error" class="mt-1 text-sm text-red-600">{{ errors.start_date.message }}</p>
            </div>
            <div class="w-full">
              <label for="end_date" class="block text-sm font-medium text-gray-900 mb-2">End Date</label>
              <CstInputFields
                  :date-options="dateOptions"
                  :value="note.end_date"
                  class="planner-date-picker cursor-pointer w-full"
                  placeholder="Select specific date range"
                  type="date"
                  :range="false"
                  :date-picker-popup-classes="['planner-date-picker-popup']"
                  :enable-auto-close="true"
                  @change="(e) => updateNoteField('end_date', e)"
              />
            </div>
          </div>
          <div class="w-1/2 pr-2">
            <label class="block text-sm font-medium text-gray-900 mb-2">Visibility</label>
                <CstDropdown class="w-full" size="small">
                  <template v-slot:selected>
                    <div class="flex flex-row space-x-2 py-1 items-center">
                      <div class="flex gap-x-3 items-center">
                        <p class="text-sm leading-none text-gray-900">{{ note.is_private ? 'Only me' : 'Public' }}</p>
                      </div>
                    </div>
                  </template>

                  <template v-slot>
                    <CstDropdownItem class="group point !pl-4 flex w-full justify-between items-center h-14" @click="updateNoteField('is_private', true)">
                      <div class="flex gap-x-3 items-center">
                        <div>
                          <p class="text-sm text-gray-900">Only me</p>
                          <p class="text-xs text-gray-700">Note will be shown to you only </p>
                        </div>
                      </div>
                    </CstDropdownItem>
                    <CstDropdownItem class="group point !pl-4 flex w-full justify-between items-center h-14" @click="updateNoteField('is_private', false)">
                      <div class="flex gap-x-3 items-center">
                        <div>
                          <p class="text-sm text-gray-900">Public</p>
                          <p class="text-xs text-gray-700">Note will be visible to all team members</p>
                        </div>
                      </div>
                    </CstDropdownItem>
                  </template>
                </CstDropdown>
              </div>
          <div class="mt-2">
            <label class="block text-sm font-medium text-gray-900 mb-2">Color</label>
            <div class="flex flex-wrap gap-2">
              <template v-for="(color, index) in colorOptions" :key="index">
                <div
                    @click="updateNoteField('note_color', color.hex)"
                    class="w-6 h-6 rounded-md cursor-pointer flex justify-center items-center transition-all duration-200 hover:scale-125 hover:shadow-md"
                    :style="{ backgroundColor: color.hex }"
                >
                  <i v-if="note.note_color === color.hex" class="fa fa-check text-white"></i>
                </div>
              </template>
            </div>
          </div>

          <div>
            <div class="flex items-center mb-4">
              <CstSwitch
                v-model="note.is_recurring"
                label="Repeat"
                :label-class="'font-medium text-gray-900'"
                size="small"
                @input="handleRecurringChange"
              />

              <img
                  src="@src/assets/img/common/new_tag.svg"
                  alt="new-tag"
                  class="ml-1"
              />

            </div>
            <div v-if="note.is_recurring" class="mt-1">
              <!-- Repeat Every X Frequency -->
              <div class="mb-4 w-1/2 flex gap-2.5 items-center pr-2">
                <span class="text-sm font-medium text-gray-900 min-w-[77px]">Repeat Every</span>
                <div class="flex items-center gap-2">
                  <div class="w-15 flex border border-gray-300 rounded-lg overflow-hidden h-10 space-x-1">
                    <button
                      class="w-6 flex items-center justify-center text-gray-700 border-0 "
                      @click="decrementIntervalValue"
                    >
                      <span class="text-lg font-bold">−</span>
                    </button>
                    <input
                      id="interval_value"
                      :value="note.interval_value"
                      type="text"
                      maxlength="2"
                      @input="handleIntervalValueInput"
                      @blur="validateCountOnBlur"
                      class="w-6 text-center text-gray-900 h-10 border-0 focus:outline-none"
                    />
                    <button
                      class="w-6 flex items-center justify-center text-blue-500 border-0"
                      @click="incrementIntervalValue"
                    >
                      <span class="text-lg font-bold">+</span>
                    </button>
                  </div>
                  <CstDropdown class="w-24" size="small">
                    <template v-slot:selected>
                      <div class="flex py-1 items-center">
                        <p class="text-sm leading-none text-gray-900">{{ getFrequencyLabel() }}</p>
                      </div>
                    </template>
                    <template v-slot>
                      <CstDropdownItem
                        class="group point !pl-4 flex w-full justify-between items-center h-10"
                        @click="updateNoteField('recurring_frequency', 'Daily')"
                      >
                        <div class="flex gap-x-3 items-center">
                          <p class="text-sm text-gray-900">day</p>
                        </div>
                      </CstDropdownItem>
                      <CstDropdownItem
                        class="group point !pl-4 flex w-full justify-between items-center h-10"
                        @click="selectWeeklyFrequency"
                      >
                        <div class="flex gap-x-3 items-center">
                          <p class="text-sm text-gray-900">week</p>
                        </div>
                      </CstDropdownItem>
                      <CstDropdownItem
                        class="group point !pl-4 flex w-full justify-between items-center h-10"
                        @click="updateNoteField('recurring_frequency', 'Monthly')"
                      >
                        <div class="flex gap-x-3 items-center">
                          <p class="text-sm text-gray-900">month</p>
                        </div>
                      </CstDropdownItem>
                    </template>
                  </CstDropdown>
                </div>
              </div>

              <!-- Weekday selectors - only show if Weekly is selected -->
              <div v-if="note.recurring_frequency === 'Weekly'" class="mb-4">
                <label class="block text-sm font-medium text-gray-900 mb-2">Repeat on</label>
                <div class="flex space-x-2">
                  <template v-for="(day, index) in ['S','M','T','W','T','F','S']" :key="index">
                    <div
                      @click="toggleWeekday(index)"
                      class="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer border"
                      :class="note.weekdays && note.weekdays.includes(index) ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-300 hover:border-blue-300'"
                    >
                      {{ day }}
                    </div>
                  </template>
                </div>
              </div>

<label class="block text-sm font-medium text-gray-900 mb-2">Ends</label>
              <!-- End condition selection -->
              <div class="mb-4">
                <div class="flex justify-between space-x-4">
                  <!-- Until date option -->
                  <div class="w-1/2 flex items-center space-x-2">
                    <CstRadio
                      id="end-until"
                      name="end-condition"
                      value="until"
                      :label-class="'!break-normal'"
                      :modelValue="note.end_condition"
                      @change="updateNoteField('end_condition', $event)"
                    >
                      On
                    </CstRadio>
                    <CstInputFields
                      :date-options="dateOptions"
                      :value="note.recurring_until_date"
                      :disabled="note.end_condition !== 'until'"
                      class="planner-date-picker cursor-pointer !w-79"
                      :class="{'opacity-50 pointer-events-none': note.end_condition !== 'until'}"
                      placeholder="Select end date"
                      type="date"
                      :allow-min-width="false"
                      :range="false"
                      :date-picker-popup-classes="['planner-date-picker-popup']"
                      :enable-auto-close="true"
                      @change="(e) => updateNoteField('recurring_until_date', e)"
                    />
                  </div>

                  <!-- Occurrences option -->
                  <div class="w-1/2 flex items-center space-x-2">
                    <CstRadio
                      id="end-count"
                      name="end-condition"
                      value="count"
                      :modelValue="note.end_condition"
                      @change="updateNoteField('end_condition', $event)"
                    >
                      After
                    </CstRadio>
                    <div class="flex items-center space-x-2">
                      <div class="w-15 flex border border-gray-300 h-10 rounded-lg overflow-hidden space-x-1" :class="{'opacity-50': note.end_condition !== 'count'}">
                        <button
                          class="w-6 flex items-center justify-center text-gray-700 border-0"
                          @click="decrementOccurrences"
                          :disabled="note.end_condition !== 'count'"
                        >
                          <span class="text-lg font-bold">−</span>
                        </button>
                        <input
                          id="recurring_count"
                          :value="note.recurring_count"
                          type="text"
                          maxlength="2"
                          @input="handleRecurringCountInput"
                          @blur="validateRecurringCountOnBlur"
                          class="w-6 text-center text-gray-900 border-0 focus:outline-none"
                          :disabled="note.end_condition !== 'count'"
                          :class="note.end_condition !== 'count' ? 'text-gray-600' : ''"
                        />
                        <button
                          class="w-6 flex items-center justify-center text-blue-500 border-0"
                          @click="incrementOccurrences"
                          :disabled="note.end_condition !== 'count'"
                        >
                          <span class="text-lg font-bold">+</span>
                        </button>
                      </div>
                      <span class="text-sm text-gray-900">occurrence{{ note.recurring_count > 1 ? 's' : '' }}</span>
                    </div>
                  </div>
                </div>
                <p v-if="errors?.recurring_count?.error" class="mt-1 text-sm text-red-600">{{ errors?.recurring_count?.message }}</p>
              </div>
            </div>

            <!-- Display recurring message -->
            <div v-if="note.is_recurring" class="relative rounded-md cstu-shadow-sm cstu-transition-all cstu-duration-200 cstu-border cstu-px-4 cstu-py-3 cstu-text-base" role="alert">
              <div class="flex items-start cstu-space-x-3">
                <div class="flex-grow">
                  <div class="mt-1 text-gray-900 cstu-text-base">{{ getRecurringNotesMessage() }}</div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex justify-between items-center pt-4 pb-2" :class="{'!justify-end': !note.id}">
            <CstButton class="ml-auto block" @click.prevent="saveNote">
              <span>{{ note.id ? 'Edit Note' : 'Add Note' }}</span>
              <clip-loader
                  v-if="loader"
                  class="spinner ml-2"
                  :color="'#e0dfdf'"
                  :size="'15px'"
              ></clip-loader>
            </CstButton>
          </div>
        </div>
      </div>
    </div>
  </CstuModal>
</template>


<script setup>
import { usePlannerNotes } from '@modules/planner_v2/composables/usePlannerNotes'
import CstButton from '@ui/Button/CstButton'
import CstInputFields from '@ui/Input/CstInputFields'
import CstDropdown from '@ui/Dropdown/CstDropdown'
import CstDropdownItem from '@ui/Dropdown/CstDropdownItem'
import CstSwitch from '@ui/Switch/CstSwitch'
import CstRadio from '@ui/Radio/CstRadio'
import { Alert } from '@contentstudio/ui'

const {
  note,
  colorOptions,
  loader,
  errors,
  closeModal,
  saveNote,
  deleteNote,
  updateNoteField,
  dateOptions,
  incrementOccurrences,
  decrementOccurrences,
  handleRecurringChange,
  toggleWeekday,
  getRecurringNotesMessage,
  selectWeeklyFrequency,
  handleRecurringCountInput,
  validateRecurringCountOnBlur,
  decrementIntervalValue,
  incrementIntervalValue,
  handleIntervalValueInput,
  getFrequencyLabel,
  validateCountOnBlur
} = usePlannerNotes()
</script>

<style lang="scss">
#planner-notes-modal {
  .cstu-modal-dialog {
    .cstumodal-content {
      .cstu-modal-header {
        border: none !important;
        border-bottom: 0px !important;
        align-items: center !important;
        padding: 1rem 2.2rem;
        h5 {
          font-size: 1.25rem;
          font-weight: 500;
        }
      }
    }
  }
}

/* Counter UI specific styles */
.planner-notes-counter-btn {
  @apply w-[48px] flex items-center justify-center;
}

.planner-notes-counter-minus {
  @apply text-gray-700;
}

.planner-notes-counter-plus {
  @apply text-blue-500;
}

.planner-notes-counter-input {
  @apply flex-1 text-center text-gray-900 px-3 py-2.5;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}
</style>
